import {Component, ElementRef, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {AutoUnsubscribe} from '../../../decorators/auto-unsubscribe.decorator';
import {NotificationsService} from '../../../services/notifications.service';
import {AgencyService} from '../../../services/agency.service';
import {combineLatest, Subscription} from 'rxjs';
import {fadeInOut, slideIn} from './animations';
import {NotificationsQuery} from '../../../common/elf-store/notifications/notifications.query';
import {
  GeneralAnnouncementDto,
  NotificationSource,
  NotificationStatus,
  NotificationType,
  UserNotificationDto,
} from '../../../types/notifications.type';
import {NotificationsDrawerService} from '../../../modules/notifications-drawer/notifications-drawer.service';
import {Router} from '@angular/router';
import {SUBSCRIPTIONS} from 'src/app/common/utils/constants';
import {UserPermissionsService} from 'src/app/services/user-permissions.service';
import {UserPermissions} from 'src/app/common/elf-store/user-permissions/user-permissions.store';
import {EventEmitterService} from 'src/app/services/event-emitter.service';
import {SiteOverviewPlanService} from '../../../services/site-overview-plan.service';
import {SitesQuery} from '../../../common/elf-store/sites/sites.query';
import {SiteSearchEntryDto} from '../../../../userway/sdk/UserWayApiClient';
import {UserInfoQuery} from '../../../common/elf-store/user-info/user-info.query';
import {Severity} from '../../../types/severity';
import {TrialSiteService} from '../../../services/trial-site.service';
import {TrialInfo} from '../../../common/elf-store/user-info/user-info.type';
import {PaymentDataService} from '../../../services/payment-data.service';

type FadeInOutState = 'show' | 'hide';
type SlideInState = 'prev' | 'current' | 'next';

@Component({
  selector: 'mc-top-notifications',
  templateUrl: './top-notifications.component.html',
  styleUrls: ['./top-notifications.component.scss'],
  animations: [fadeInOut, slideIn],
})
@AutoUnsubscribe()
export class TopNotificationsComponent implements OnInit, OnDestroy {
  @ViewChild('componentRootRef') componentRootRef: ElementRef;
  notificationSource = NotificationSource;
  notificationType = NotificationType;
  /* eslint-disable-next-line local/no-subscription-variables */
  loadNotificationSubscription: Subscription;
  announcements: GeneralAnnouncementDto[] = [];
  personalNotifications: UserNotificationDto[] = [];
  intervalID = 0;
  prevIndex = 0;
  currentIndex = 0;
  nextIndex = 0;
  autoplayTimeout = 20000;
  isLoading: boolean;

  readonly NotificationsService = NotificationsService;
  /* eslint-disable-next-line local/no-subscription-variables */
  readNotificationSub: Subscription;
  currentSite: SiteSearchEntryDto = null;

  constructor(
    public userInfo: UserInfoQuery,
    public agencyService: AgencyService,
    private router: Router,
    private sitesQuery: SitesQuery,
    private trialSiteService: TrialSiteService,
    private notificationsQuery: NotificationsQuery,
    private eventEmitterService: EventEmitterService,
    private notificationsService: NotificationsService,
    private userPermissionsService: UserPermissionsService,
    private siteOverviewPlanService: SiteOverviewPlanService,
    private notificationsDrawerService: NotificationsDrawerService,
    private paymentDataService: PaymentDataService,
  ) {}

  isTrialNoCardNotification(item: UserNotificationDto, trialInfo: TrialInfo): boolean {
    return (
      item.type === NotificationType.AccountBillingAlertsNoCardTrialExpires &&
      !NotificationsService.shouldShowTrialNoCardExpiredNotification(item, trialInfo) &&
      trialInfo.isTrialNoCard
    );
  }

  shouldShowUpgradeBtn(item: UserNotificationDto): boolean {
    return item.type === NotificationType.AccountBillingAlertsNoCardTrialExpires;
  }

  get notificationList() {
    return [...this.personalNotifications, ...this.announcements];
  }

  getNotificationText(item: UserNotificationDto, trialInfo: TrialInfo): string {
    return NotificationsService.getBaseTextNotification(item, trialInfo);
  }

  getSeverity(item: UserNotificationDto | GeneralAnnouncementDto, trialInfo: TrialInfo): Severity {
    return NotificationsService.getSeverity(item, trialInfo, this.agencyService.agencyMode);
  }

  ngOnInit(): void {
    this.loadNotifications();
    this.notificationsQuery.isLoading$.subscribe(isLoading => (this.isLoading = isLoading));

    this[SUBSCRIPTIONS].add(
      this.notificationsQuery.personalNotDismissedNotifications$.subscribe(
        (notificationList: UserNotificationDto[]) => {
          this.personalNotifications = notificationList;
          this.startAnimation();
        },
      ),
    );
    this[SUBSCRIPTIONS].add(
      this.notificationsQuery.generalAnnouncements$.subscribe((notificationList: GeneralAnnouncementDto[]) => {
        this.announcements = notificationList;
      }),
    );

    this[SUBSCRIPTIONS].add(
      combineLatest([this.sitesQuery.activeSites$, this.userInfo.userInfo$]).subscribe(([sitesData, userInfo]) => {
        const site = sitesData.sites[sitesData.sites.length - 1];
        if (!site) return;
        this.currentSite = site;
        if (userInfo.trialInfo.isTrialNoCard) {
          this.siteOverviewPlanService.initPlanData(site);
        }
      }),
    );

    this[SUBSCRIPTIONS].add(
      this.paymentDataService.successfullyPaid$.subscribe(isPaid => {
        if (isPaid) {
          this.readNoCardTrialNotifications();
        }
      }),
    );
  }

  loadNotifications(unsubscribe: boolean = false) {
    this.loadNotificationSubscription = this.notificationsService.getAllNotifications().subscribe();
    if (unsubscribe) {
      this.loadNotificationSubscription?.unsubscribe();
    }
  }

  startAnimation() {
    this.startAutoPlay();
    setTimeout(() => {
      this.updateTabIndex();
    }, 0);
  }

  updateTabIndex() {
    const elements = this.componentRootRef.nativeElement.getElementsByClassName('notif-content');
    if (elements?.length > 0) {
      for (let index = 0; index < elements.length; index++) {
        const list = elements[index].getElementsByTagName('a');
        if (list?.length > 0) {
          const linkEl = list[0];
          if (this.currentIndex === index) {
            linkEl.setAttribute('tabindex', 0);
          } else {
            linkEl.setAttribute('tabindex', -1);
          }
        }
      }
    }
  }

  startAutoPlay() {
    if (this.intervalID === 0) {
      this.updateState();
      this.intervalID = window.setInterval(() => {
        this.tick();
      }, this.autoplayTimeout);
    }
  }

  tick() {
    this.currentIndex += 1;
    this.updateState();
    this.updateTabIndex();
  }

  updateState() {
    const firstIndex = 0;
    const lastIndex = this.notificationList.length - 1;

    if (this.currentIndex >= this.notificationList.length) {
      this.currentIndex = 0;
    }

    if (this.currentIndex === firstIndex) {
      this.prevIndex = lastIndex;
    } else {
      this.prevIndex = this.currentIndex - 1;
    }

    if (this.currentIndex === lastIndex) {
      this.nextIndex = firstIndex;
    } else {
      this.nextIndex = this.currentIndex + 1;
    }
  }

  getFadeInOutState(index: number): FadeInOutState {
    return this.currentIndex === index ? 'show' : 'hide';
  }

  getSlideInState(index: number): SlideInState {
    if (this.prevIndex === index && this.prevIndex !== this.currentIndex) {
      return 'next';
    }

    if (this.currentIndex === index) {
      return 'current';
    }

    if (this.nextIndex === index) {
      return 'prev';
    }

    return 'current';
  }

  goToBillingSetting(event: Event, id: string) {
    this.readNotification(id, () => {
      event.stopPropagation();
      this[SUBSCRIPTIONS].add(
        this.userPermissionsService
          .checkUserPermissions(UserPermissions.manageBilling.billingInvoicesPage)
          .subscribe(isUserHavePermission => {
            if (isUserHavePermission) {
              this.router.navigate(['settings/billing-invoices']);
            } else {
              this.eventEmitterService.showForbiddenDialog.next(true);
            }
          }),
      );
    });
  }

  showPaymentForm() {
    this.trialSiteService.upgrade(this.currentSite);
  }

  openNotificationsDrawer(item: UserNotificationDto | GeneralAnnouncementDto) {
    if ('type' in item && item.type === NotificationType.AccountBillingAlertsNoCardTrialExpires) {
      this.notificationsDrawerService.open();
      return;
    }

    this.readNotification(item.id, () => {
      this.notificationsDrawerService.open();
    });
  }

  readNotification(id: string, callback: () => void) {
    this.readNotificationSub = this.notificationsService.readNotification(id, NotificationStatus.READ).subscribe(() => {
      this.loadNotifications(true);
      callback();
    });

    setTimeout(() => {
      this.readNotificationSub?.unsubscribe();
    });
  }

  ngOnDestroy() {
    this.loadNotificationSubscription?.unsubscribe();
    this.personalNotifications = [];
    this.announcements = [];
    window.clearInterval(this.intervalID);
  }

  isGeneralAnnouncement(item: any): item is GeneralAnnouncementDto {
    return item && item.htmlContent !== undefined;
  }

  isUserNotification(item: any): item is UserNotificationDto {
    return item && item.payload !== undefined;
  }

  private readNoCardTrialNotifications() {
    if (this.userInfo.trialInfo().isTrialNoCard || this.userInfo.trialInfo().isTrialNoCardExpired) {
      const ids = this.notificationsQuery
        .personalNotDismissedNotificationsSignal()
        .filter(item => item.status === NotificationStatus.NEW)
        .map(el => el.id);
      this.notificationsService.readAllNotifications(ids, NotificationStatus.READ).subscribe();
    }
  }
}
